/**
 * SEO/GEO helpers for Klapzo Kit method pages (PRD §2.1 / task 2.3): schema.org
 * HowTo JSON-LD (what answer engines cite) + hreflang alternates. Pure.
 */
import type { Method, KitLocale } from "./types.js";

const LOCALE_HREFLANG: Record<KitLocale, string> = {
  "pt-BR": "pt-BR",
  "en-US": "en-US",
  "es-LATAM": "es-419",
};

/** Strip basic markdown to plain text for meta descriptions. */
export function markdownToText(md: string): string {
  return md
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/[*_`#>]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Extract numbered "Passos" as HowTo steps, if present. */
export function extractSteps(md: string): string[] {
  return md
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => /^\d+\.\s+/.test(l))
    .map((l) => l.replace(/^\d+\.\s+/, ""));
}

export interface JsonLdHowTo {
  "@context": "https://schema.org";
  "@type": "HowTo";
  name: string;
  description: string;
  inLanguage: string;
  totalTime?: string;
  step?: Array<{ "@type": "HowToStep"; position: number; text: string }>;
}

export function buildMethodJsonLd(method: Method): JsonLdHowTo {
  const ld: JsonLdHowTo = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: method.title,
    description: markdownToText(method.bodyMd).slice(0, 300),
    inLanguage: LOCALE_HREFLANG[method.locale],
  };
  if (method.durationMin && method.durationMin > 0) ld.totalTime = `PT${method.durationMin}M`;
  const steps = extractSteps(method.bodyMd);
  if (steps.length > 0) {
    ld.step = steps.map((text, i) => ({ "@type": "HowToStep", position: i + 1, text }));
  }
  return ld;
}

export interface HreflangLink {
  hreflang: string;
  href: string;
}

/**
 * Build hreflang alternates for a method available in several locales.
 * `baseUrl` like "https://kit.klapzo.app"; path convention /{locale}/m/{slug}.
 */
export function buildHreflangLinks(
  slug: string,
  availableLocales: readonly KitLocale[],
  baseUrl: string,
): HreflangLink[] {
  const base = baseUrl.replace(/\/$/, "");
  const links = availableLocales.map((loc) => ({
    hreflang: LOCALE_HREFLANG[loc],
    href: `${base}/${loc}/m/${slug}`,
  }));
  // x-default points at the pt-BR canonical (the library's primary market).
  if (availableLocales.includes("pt-BR")) {
    links.push({ hreflang: "x-default", href: `${base}/pt-BR/m/${slug}` });
  }
  return links;
}
